// third-party libs
import React, { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import { useFormContext } from 'react-hook-form'
import { Box, Typography, Button, Stack, IconButton, Badge } from '@mui/material'
import { makeStyles } from '@mui/styles'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import CalculateIcon from '@mui/icons-material/CalculateOutlined'
import { randomId } from '@mui/x-data-grid-generator'
import { capitalize } from 'lodash'

// specific
import AxleWeightsModal from 'components/containers/inbound-tickets/components/AxleWeightsModal'

// generic
import BasicDataGrid from 'components/generic/datagrid'
import MaterialAutocomplete from 'components/generic/form/AsyncAutocomplete/MaterialAutocomplete'
import FinishedGoodsModal from 'components/generic/modals/dialog-modal/FinishedGoodsModal'
import PackingListsModal from 'components/generic/modals/dialog-modal/PackingListsModal'
import AlertModal from 'components/generic/modals/dialog-modal/AlertModal'
import EditableCell from 'components/generic/datagrid/cells/EditableCell'
import InputFormat from 'components/generic/inputs/formatting/InputFormat'
import ScaleButton from 'components/generic/buttons/ScaleButton'
import CameraButton from 'components/generic/buttons/CameraButton'
import ScalePopover from 'components/generic/popovers/ScalePopover'
import MultiImageBlade from 'components/generic/modals/blade-modal/MultiImageBlade'

// helpers and utils
import { useScaleState } from 'utils/custom-hooks/useScaleState'
import { formatNumber } from 'utils/helpers'
import { generateRequestImagesFunction } from 'utils/images'
import { DEFAULT_AXLE_DATA } from 'utils/constants'
import { formatAxleWeightsFromFormData } from 'components/containers/inbound-tickets/helpers/axleWeightsHelpers'
import { formatMaterialName } from 'utils/data-manipulation/valueFormatter'

const useStyles = makeStyles(theme => ({
  root: {
    border: 'none',
    // remove header padding to align headers with column values
    '& .MuiDataGrid-columnHeaderTitleContainer': {
      padding: theme.spacing(0),
    },
    '& .MuiDataGrid-columnHeader': {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
    '& .MuiDataGrid-cell': {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
    '& .MuiDataGrid-columnSeparator': {
      visibility: 'hidden',
    },
    // replace default hover / active-row styling from datagrid
    '& .MuiDataGrid-row': {
      '&.total-row': {
        backgroundColor: theme.palette.info.lightBg,
        '&:hover': {
          backgroundColor: theme.palette.info.lightBg,
        },
      },
      '&:not(.total-row)': {
        '&.Mui-selected': {
          backgroundColor: 'inherit',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.08)',
          },
        },
        '&:not(.Mui-selected)': {
          backgroundColor: 'inherit',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.08)',
          },
        },
      },
    },
    '& .MuiDataGrid-columnHeader:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell:focus-within': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell.button-cell': {
      paddingLeft: 0,
      paddingRight: 0,
    },
  },
}))

const ActiveMaterialList = ({
  priceSheetsData,
  salesOrderData,
  onUpdateMaterial,
  isUpdate,
  materials,
  totalInformation,
  loading,
  apiRef,
  totalApiRef,
  setFocus,
  isTransfer,
  disabled,
}) => {
  const classes = useStyles()
  const { t } = useTranslation('inboundTicket')

  const { control, setValue } = useFormContext()

  const [openFinishedGoods, setOpenFinishedGoods] = useState(false)
  const [openPackingLists, setOpenPackingLists] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedPLId, setSelectedPLId] = useState()
  const [packingLists, setPackingLists] = useState([])
  const [scalePopoverData, setScalePopoverData] = useState({})
  const [multiImageBladeData, setMultiImageBladeData] = useState(null)
  const [activeRowForAxleWeights, setActiveRowForAxleWeights] = useState(null)

  const { captureWeightsOBTFeatureFlagOn, useMinimalObt, removeZerosFF, enablePullApartFeatures } = useSelector(
    state => state.featureFlags?.variables || {},
  )

  const { defaultYardUm, settings } = useSelector(state => state.settings)
  const { disableUmDropdowns, enableAxleWeights, defaultNumberOfAxles } =
    settings?.purchaseSettings?.inboundTickets || {}

  const { consumerInfo: consumerInfoState } = useSelector(state => state.outboundTicket)
  const salesOrderId = consumerInfoState?.salesOrderId

  const { isFetchingScaleReading, showScaleButton, readScale } = useScaleState()

  const { requestImages } = generateRequestImagesFunction()

  const { devices, selectedCameraId, selectedScaleIds, selectedCameraIds, scaleCameras } = useSelector(
    state => state.devices,
  )

  const useNativeCamera =
    selectedCameraIds?.length === 0 && Object.values(scaleCameras || {}).every(cameras => !cameras?.length)

  const handleDeleteClick = useCallback(
    id => event => {
      event.stopPropagation()

      const row = apiRef.current.getRow(id)

      if (row?.plId) {
        setSelectedPLId(row?.plId)
        setShowDeleteModal(true)
      } else {
        apiRef.current.updateRows([{ id, _action: 'delete' }])
        onUpdateMaterial()
      }
    },
    [packingLists],
  )

  const onRemovePackingListRow = useCallback(() => {
    if (selectedPLId) {
      const rows = materials.filter(row => row?.plId === selectedPLId)

      rows.forEach(row => apiRef.current.updateRows([{ id: row?.id, _action: 'delete' }]))

      setShowDeleteModal(false)
      onUpdateMaterial()
    }
  }, [packingLists, apiRef, selectedPLId])

  const handleChange = (event, row) => {
    const newValue = event?.target?.value
    const ref = row.id === 'truck-total' ? totalApiRef : apiRef
    ref.current.updateRows([
      {
        ...row,
        [event.target.name]: newValue,
      },
    ])
  }

  const handleRequestImages = useCallback(
    (row, ref, weightType, scaleId) => {
      if (!useNativeCamera && (row[`${weightType}Images`] || []).length === 0) {
        requestImages(imageData => {
          ref.current.updateRows([{ id: row.id, [`${weightType}Images`]: imageData }])
          setValue(`materialGrid.${row.id}.${weightType}Images`, imageData)
          onUpdateMaterial()
        }, scaleId)
      }
    },
    [requestImages, selectedCameraIds, setValue, onUpdateMaterial],
  )

  const callReadScale = ({ material, weightType, scaleId = '' }) => {
    readScale(scaleData => {
      const isTruck = material.id === 'truck-total'
      const ref = isTruck ? totalApiRef : apiRef

      const weightField = `yard${capitalize(weightType)}`
      ref.current.updateRows([{ ...material, [weightField]: scaleData?.weight, axleWeights: [], _source: 'scale' }])
      setFocus(`materialGrid.${material.id}.${weightField}`)

      // Add image request after scale reading
      handleRequestImages(material, ref, weightType, scaleId)
    }, scaleId)
  }

  const scaleButtonClickHandler = (material, weightType, event) => {
    if (!['gross', 'tare'].includes(weightType)) throw new Error('Invalid weight type!')

    if (selectedScaleIds?.length > 1) {
      setScalePopoverData({
        anchorEl: event.target,
        material,
        weightType,
      })
    } else {
      callReadScale({ material, weightType, scaleId: selectedScaleIds[0] })
    }
  }

  const materialColumns = useMemo(() => {
    const columns = [
      {
        field: 'code',
        headerName: 'Material',
        minWidth: 200,
        flex: 3,
        renderCell: params => {
          const { row } = params
          const { code, plId, fgId } = row

          if (row.id === 'truck-total') {
            return t('truck_total')
          }

          if (row.id === 'load-total') {
            return row.code
          }

          if (disabled) {
            const salesOrderEntry = salesOrderData?.priceSheet?.entriesObj?.find(entry => entry.id === row.orderEntryId)
            return formatMaterialName({
              commonName: code?.materialName,
              code: code?.materialCode,
              overrideName: salesOrderEntry?.overrideName,
            })
          }
          return (
            <MaterialAutocomplete
              variant="outlined"
              size="small"
              defaultValue={{
                commonName: code?.materialName,
                code: code?.materialCode,
                materialTypeId: code?.materialTypeId,
                orderEntryId: code?.orderEntryId,
              }}
              onChange={value => {
                if (!value) return

                const { id, code: materialCode, commonName: materialName, defaultPriceEntry, orderEntryId } = value
                apiRef.current.updateRows([
                  {
                    ...row,
                    code: {
                      ...defaultPriceEntry,
                      materialName,
                      materialCode,
                      orderEntryId,
                    },
                    materialTypeId: id,
                    orderEntryId,
                    materialName: materialName || '',
                    um: defaultYardUm,
                    isNew: false,
                  },
                ])
              }}
              onUpdateMaterial={onUpdateMaterial}
              orderData={salesOrderId && salesOrderId === salesOrderData?.id ? salesOrderData : null}
              isFinishedGoodOrPackingList={plId || fgId}
            />
          )
        },
        sortable: false,
      },
      {
        field: 'axle-weight-button',
        headerName: '',
        sortable: false,
        width: 50,
        align: 'right',
        cellClassName: 'button-cell',
        renderCell: params => {
          const { row } = params
          const { id, axleWeights = [], plId, fgId } = row

          if (id === 'load-total' || plId || fgId) {
            return null
          }

          return (
            <IconButton
              size="small"
              style={{ padding: '4px' }}
              onClick={() => setActiveRowForAxleWeights({ ...row, gross: row.yardGross, tare: row.yardTare })}
            >
              <Badge color="primary" variant="dot" invisible={axleWeights?.length === 0}>
                <CalculateIcon />
              </Badge>
            </IconButton>
          )
        },
      },
      {
        field: 'um',
        headerName: 'UM',
        headerAlign: 'left',
        align: 'left',
        sortable: false,
      },
      {
        field: 'plTag',
        headerName: 'PL ID',
        headerAlign: 'left',
        align: 'left',
        sortable: false,
      },
      {
        field: 'fgId',
        headerName: 'FG ID',
        width: 135,
        headerAlign: 'left',
        align: 'left',
        sortable: false,
      },
      {
        field: 'fgType',
        headerName: 'FG Type',
        sortable: false,
      },
      {
        field: 'itemCount',
        headerName: 'Count',
        sortable: false,
      },
      {
        field: 'yardGross',
        headerName: `${useMinimalObt ? 'Gross (OUT)' : 'Yard Gross'}`,
        width: 145,
        type: 'number',
        headerAlign: 'right',
        align: 'right',
        renderCell: params => {
          const { row = {} } = params
          const { yardGross, id } = row
          if (id === 'load-total' || disabled) {
            return formatNumber(yardGross)
          }
          const ref = id === 'truck-total' ? totalApiRef : apiRef
          return (
            <EditableCell
              control={control}
              name={`materialGrid.${id}.yardGross`}
              defaultValue={yardGross || yardGross === 0 ? String(yardGross) : ''}
              isEdit
              size="small"
              {...(removeZerosFF && {
                onFocus: event => {
                  if (event.target.value === '0') {
                    setValue(`materialGrid.${id}.yardGross`, '')
                    ref.current.updateRows([{ id, yardGross: '' }])
                  }
                  setTimeout(() => event.target.focus(), 100)
                },
              })}
              {...(removeZerosFF && {
                onBlur: event => {
                  if (event.target.value === '') {
                    setValue(`materialGrid.${id}.yardGross`, '0')
                    ref.current.updateRows([{ id, yardGross: '0' }])
                  }
                },
              })}
              InputProps={{
                inputComponent: InputFormat,
                inputProps: {
                  name: 'yardGross',
                  inputType: 'number',
                  onChange: event => {
                    if (params.row._source === 'scale') {
                      // Clear the _source flag
                      ref.current.updateRows([{ id: params.row.id, _source: undefined }])
                      return
                    }
                    if (Number(event.target.value)) {
                      handleRequestImages(row, ref, 'gross')
                    }
                    handleChange(event, row)
                    if (event.source === 'event') {
                      ref.current.updateRows([{ id, axleWeights: [] }])
                    }
                  },
                  style: {
                    fontSize: '0.875rem',
                    textAlign: 'right',
                  },
                },
              }}
            />
          )
        },
        sortable: false,
      },
      {
        field: 'iot-buttons-gross',
        headerName: '',
        sortable: false,
        width: 64,
        cellClassName: 'button-cell',
        renderCell: params => {
          const { id, plId, fgId, axleWeights } = params.row

          if (id === 'load-total' || plId || fgId) return ''

          return (
            <>
              {captureWeightsOBTFeatureFlagOn && showScaleButton ? (
                <ScaleButton
                  disabled={disabled || isFetchingScaleReading}
                  onClick={event => scaleButtonClickHandler(params.row, 'gross', event)}
                />
              ) : null}
              <CameraButton
                showBadge={!!params.row.grossImages?.length > 0}
                handleCameraClick={() => {
                  const newMultiImageBladeData = { ...params.row }
                  if (axleWeights?.length > 1) {
                    newMultiImageBladeData.axleNumber = 1
                  }
                  setMultiImageBladeData(newMultiImageBladeData)
                }}
                disabled={
                  selectedCameraIds?.length > 0 && !params.row.grossImages?.length > 0 && !(axleWeights?.length > 1)
                }
              />
            </>
          )
        },
      },
      {
        field: 'yardTare',
        headerName: `${useMinimalObt ? 'Tare (IN)' : 'Yard Tare'}`,
        width: 145,
        type: 'number',
        headerAlign: 'right',
        align: 'right',
        renderCell: params => {
          const { row = {} } = params
          const { yardTare, id } = row
          if (id === 'load-total' || disabled) {
            return formatNumber(yardTare)
          }
          const ref = id === 'truck-total' ? totalApiRef : apiRef
          return (
            <EditableCell
              control={control}
              name={`materialGrid.${id}.yardTare`}
              defaultValue={yardTare || yardTare === 0 ? String(yardTare) : ''}
              isEdit
              size="small"
              {...(removeZerosFF && {
                onFocus: event => {
                  if (event.target.value === '0') {
                    setValue(`materialGrid.${id}.yardTare`, '')
                    ref.current.updateRows([{ id, yardTare: '' }])
                  }
                  setTimeout(() => event.target.focus(), 100)
                },
              })}
              {...(removeZerosFF && {
                onBlur: event => {
                  if (event.target.value === '') {
                    setValue(`materialGrid.${id}.yardTare`, '0')
                    ref.current.updateRows([{ id, yardTare: '0' }])
                  }
                },
              })}
              InputProps={{
                inputComponent: InputFormat,
                inputProps: {
                  name: 'yardTare',
                  inputType: 'number',
                  onChange: event => {
                    if (params.row._source === 'scale') {
                      // Clear the _source flag
                      ref.current.updateRows([{ id: params.row.id, _source: undefined }])
                      return
                    }
                    if (Number(event.target.value)) {
                      handleRequestImages(row, ref, 'tare')
                    }
                    handleChange(event, row)
                    if (event.source === 'event') {
                      ref.current.updateRows([{ id, axleWeights: [] }])
                    }
                  },
                  style: {
                    fontSize: '0.875rem',
                    textAlign: 'right',
                  },
                },
              }}
            />
          )
        },
        sortable: false,
      },
      {
        field: 'iot-buttons-tare',
        headerName: '',
        sortable: false,
        width: 64,
        cellClassName: 'button-cell',
        renderCell: params => {
          const { id, plId, fgId, axleWeights } = params.row

          if (id === 'load-total' || plId || fgId) return ''

          return (
            <>
              {captureWeightsOBTFeatureFlagOn && showScaleButton ? (
                <ScaleButton
                  disabled={disabled || isFetchingScaleReading}
                  onClick={event => scaleButtonClickHandler(params.row, 'tare', event)}
                />
              ) : null}
              <CameraButton
                showBadge={!!params.row.tareImages?.length > 0}
                handleCameraClick={() => {
                  const newMultiImageBladeData = { ...params.row }
                  if (axleWeights?.length > 1) {
                    newMultiImageBladeData.axleNumber = 1
                  }
                  setMultiImageBladeData(newMultiImageBladeData)
                }}
                disabled={
                  selectedCameraIds?.length > 0 && !params.row.tareImages?.length > 0 && !(axleWeights?.length > 1)
                }
              />
            </>
          )
        },
      },
      {
        field: 'yardNet',
        headerName: `${useMinimalObt ? 'Net' : 'Yard Net'}`,
        width: 135,
        type: 'number',
        headerAlign: 'right',
        align: 'right',
        valueGetter: params => {
          const { yardGross = 0, yardTare = 0 } = params.row
          return Number(yardGross) - Number(yardTare)
        },
        sortable: false,
      },
      {
        field: 'actions',
        headerName: '',
        renderCell: params => {
          const { row } = params
          return ['load-total', 'truck-total'].indexOf(row.id) > -1 || disabled ? null : (
            <div>
              <IconButton
                color="error"
                size="small"
                sx={{
                  fill: 'red',
                }}
                aria-label="delete"
                onClick={handleDeleteClick(params.row.id)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </div>
          )
        },
        headerAlign: 'center',
        align: 'center',
        sortable: false,
        width: 30,
      },
    ]
    const filteredColumns = columns.filter(column => {
      if (disableUmDropdowns && column.field === 'um') return false
      if (useMinimalObt && ['plTag', 'fgId', 'fgType'].includes(column.field)) return false
      if (!enablePullApartFeatures && column.field === 'itemCount') return false
      if (!enableAxleWeights && column.field === 'axle-weight-button') return false
      return true
    })
    return filteredColumns
  }, [
    salesOrderData,
    salesOrderId,
    packingLists,
    materials,
    disabled,
    showScaleButton,
    isFetchingScaleReading,
    selectedCameraId,
    selectedScaleIds,
    selectedCameraIds,
    devices,
    useMinimalObt,
    disableUmDropdowns,
    enableAxleWeights,
    removeZerosFF,
    handleRequestImages,
    enablePullApartFeatures,
    defaultYardUm,
  ])

  const onClickNewMaterial = useCallback(() => {
    const id = randomId()
    const updatedRow = {
      id,
      isNew: true,
      yardGross: 0,
      yardTare: 0,
    }
    if (enableAxleWeights) {
      updatedRow.axleWeights = [...Array(defaultNumberOfAxles).keys()].map((_, index) => ({
        ...DEFAULT_AXLE_DATA,
        axleNumber: index + 1,
      }))
    }
    apiRef.current.updateRows([updatedRow])
    onUpdateMaterial()
    // Wait for the grid to render with the new row

    apiRef.current.scrollToIndexes({
      rowIndex: apiRef.current.getRowsCount() - 1,
    })
  }, [apiRef, enableAxleWeights, defaultNumberOfAxles])

  const onFirstRowAdd = useCallback(() => {
    if (!isUpdate && apiRef && apiRef.current?.getRowsCount?.() === 0) {
      setTimeout(() => {
        const id = randomId()
        const updatedRow = {
          id,
          isNew: true,
          yardGross: 0,
          yardTare: 0,
        }
        if (enableAxleWeights) {
          updatedRow.axleWeights = [...Array(defaultNumberOfAxles).keys()].map((_, index) => ({
            ...DEFAULT_AXLE_DATA,
            axleNumber: index + 1,
          }))
        }
        apiRef.current?.updateRows([updatedRow])

        apiRef.current?.scrollToIndexes({
          rowIndex: apiRef.current.getRowsCount() - 1,
        })
      }, 200)
    }
  }, [apiRef, isUpdate, enableAxleWeights, defaultNumberOfAxles])

  const getFilteredPackingLists = useCallback(() => {
    if (packingLists && apiRef && apiRef.current?.getRowModels) {
      const tagList = []
      apiRef.current?.getRowModels().forEach(row => {
        if (row.plId !== undefined) {
          tagList.push(row.plId)
        }
      })
      const plList = []
      packingLists.forEach(pl => {
        if (!tagList.includes(pl.plId)) {
          plList.push(pl)
        }
      })
      return plList.filter(pl => pl.fgs?.length > 0)
    }

    return packingLists
  }, [packingLists, apiRef])

  const styleProps = {
    '.MuiDataGrid-cell': {}, // remove !important paddingLeft styling
  }

  return (
    <>
      <AlertModal
        open={showDeleteModal}
        modalType="void"
        title="Remove Finished Good on a Packing List"
        question="Are you sure you want to remove this Finished Good?"
        description="Because this Finished Good is on a Packing List, the entire packing list will be removed from this outbound ticket."
        confirmLabel="YES, REMOVE PACKING LIST"
        cancelLabel="GO BACK"
        onClose={() => {
          setShowDeleteModal(false)
        }}
        onSubmit={onRemovePackingListRow}
      />

      <Box p={3} data-testid="materials-list">
        <Stack direction="row" mb={1}>
          <Typography variant="h6" gutterBottom color="textSecondary">
            Materials
          </Typography>
          {disabled || useMinimalObt ? null : (
            <Box flex={1}>
              <Stack direction="row" spacing={3} justifyContent="flex-end">
                <Button
                  startIcon={<AddIcon />}
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setOpenFinishedGoods(true)
                  }}
                >
                  BULK ADD FINISHED GOODS
                </Button>
                {isTransfer ? null : (
                  <Button
                    startIcon={<AddIcon />}
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setOpenPackingLists(true)
                    }}
                  >
                    ADD EXISTING PACKING LISTS
                  </Button>
                )}
              </Stack>
            </Box>
          )}
        </Stack>
        <BasicDataGrid
          className={classes.root}
          loading={loading}
          rows={materials}
          columns={materialColumns}
          onCellClick={(params, event) => {
            event.stopPropagation()

            apiRef.current.state.focus.cell = null
            totalApiRef.current.state.focus.cell = null
          }}
          onRowEditCommit={onUpdateMaterial}
          apiRef={apiRef}
          editMode="row"
          disableColumnMenu
          hideFooter
          components={{
            LoadingOverlay: () => null,
          }}
          onMounted={onFirstRowAdd}
          styleProps={styleProps}
        />
        {disabled ? null : (
          <Box mt={1}>
            <Button startIcon={<AddIcon />} onClick={onClickNewMaterial} data-testid="active-material-new-button">
              Add New Material
            </Button>
          </Box>
        )}
        <BasicDataGrid
          className={classes.root}
          loading={loading}
          rows={totalInformation}
          columns={materialColumns}
          onCellClick={(params, event) => {
            event.stopPropagation()

            apiRef.current.state.focus.cell = null
            totalApiRef.current.state.focus.cell = null
          }}
          headerHeight={0}
          hideFooter
          getRowClassName={params => (params.row.id === 'load-total' ? 'total-row' : 'normal-row')}
          apiRef={totalApiRef}
          dataTestId="load-totals"
          styleProps={styleProps}
        />
        {openFinishedGoods && (
          <FinishedGoodsModal
            title="Bulk Add Finished Goods"
            buttonTitle="ADD"
            apiRef={apiRef}
            priceSheetsData={priceSheetsData}
            onUpdateMaterial={onUpdateMaterial}
            open={openFinishedGoods}
            onClose={() => setOpenFinishedGoods(false)}
          />
        )}
        {openPackingLists ? (
          <PackingListsModal
            title="Add Existing Packing Lists"
            buttonTitle="ADD TO TICKET"
            apiRef={apiRef}
            packingLists={getFilteredPackingLists()}
            priceSheetsData={priceSheetsData}
            onUpdateMaterial={onUpdateMaterial}
            setPackingLists={setPackingLists}
            open={openPackingLists}
            onClose={() => setOpenPackingLists(false)}
          />
        ) : null}
        <ScalePopover
          scalePopoverData={scalePopoverData}
          setScalePopoverData={setScalePopoverData}
          callReadScale={callReadScale}
        />
        <MultiImageBlade
          multiImageBladeData={multiImageBladeData}
          setMultiImageBladeData={setMultiImageBladeData}
          generateSaveImagesFunction={imageType => imageData => {
            const ref = multiImageBladeData.id === 'truck-total' ? totalApiRef : apiRef
            const { axleNumber } = multiImageBladeData
            if (axleNumber) {
              const currentAxleWeightsArray = multiImageBladeData.axleWeights || []
              const axleData = currentAxleWeightsArray?.find(axle => axle.axleNumber === axleNumber)
              const updatedAxleWeights = currentAxleWeightsArray.filter(axle => axle.axleNumber !== axleNumber)
              updatedAxleWeights.push({
                ...axleData,
                [`${imageType}Images`]: imageData,
              })
              if (axleNumber === 1) {
                ref.current.updateRows([
                  { id: multiImageBladeData.id, axleWeights: updatedAxleWeights, [`${imageType}Images`]: imageData },
                ])
                setValue(`materialGrid.${multiImageBladeData.id}.${imageType}Images`, imageData)
              } else {
                ref.current.updateRows([{ id: multiImageBladeData.id, axleWeights: updatedAxleWeights }])
              }
              setMultiImageBladeData(prevMultiImageBladeData => ({
                ...prevMultiImageBladeData,
                axleWeights: updatedAxleWeights,
                [`${imageType}Images`]: imageData,
              }))
            } else {
              ref.current.updateRows([{ ...multiImageBladeData, [`${imageType}Images`]: imageData }])
              setValue(`materialGrid.${multiImageBladeData.id}.${imageType}Images`, imageData)
              setMultiImageBladeData(ref.current.getRow(multiImageBladeData.id))
            }
            onUpdateMaterial()
          }}
          disabledUploadImages={disabled}
        />
        <AxleWeightsModal
          onClose={() => setActiveRowForAxleWeights(null)}
          open={!!activeRowForAxleWeights}
          materialData={activeRowForAxleWeights}
          onSave={(data, numberOfAxles) => {
            const gridApiRef = activeRowForAxleWeights?.id === 'truck-total' ? totalApiRef : apiRef

            if (numberOfAxles === 1) {
              setValue(`materialGrid.${activeRowForAxleWeights?.id}.yardGross`, data.axles?.['axle-1']?.gross || 0, {
                shouldDirty: true,
              })
              setValue(`materialGrid.${activeRowForAxleWeights?.id}.yardTare`, data.axles?.['axle-1']?.tare || 0)
              setValue(
                `materialGrid.${activeRowForAxleWeights?.id}.grossImages`,
                data.axles?.['axle-1']?.grossImages || [],
              )
              setValue(
                `materialGrid.${activeRowForAxleWeights?.id}.tareImages`,
                data.axles?.['axle-1']?.tareImages || [],
              )
              gridApiRef.current.updateRows([
                {
                  id: activeRowForAxleWeights?.id,
                  yardGross: data.axles?.['axle-1']?.gross || 0,
                  yardTare: data.axles?.['axle-1']?.tare || 0,
                  isManualGross: data.axles?.['axle-1']?.isManualGross || false,
                  isManualTare: data.axles?.['axle-1']?.isManualTare || false,
                  grossImages: data.axles?.['axle-1']?.grossImages || [],
                  tareImages: data.axles?.['axle-1']?.tareImages || [],
                  axleWeights: [],
                },
              ])
            } else {
              const oldAxleWeights = activeRowForAxleWeights?.axleWeights || []
              const {
                axleWeights: newAxleWeights,
                aggregateGross,
                aggregateTare,
                aggregateIsManualGross,
                aggregateIsManualTare,
              } = formatAxleWeightsFromFormData(data, numberOfAxles, oldAxleWeights)

              setValue(`materialGrid.${activeRowForAxleWeights?.id}.yardGross`, aggregateGross, { shouldDirty: true })
              setValue(`materialGrid.${activeRowForAxleWeights?.id}.yardTare`, aggregateTare)
              setValue(`materialGrid.${activeRowForAxleWeights?.id}.grossImages`, [])
              setValue(`materialGrid.${activeRowForAxleWeights?.id}.tareImages`, [])
              gridApiRef.current.updateRows([
                {
                  id: activeRowForAxleWeights?.id,
                  yardGross: aggregateGross,
                  yardTare: aggregateTare,
                  isManualGross: aggregateIsManualGross,
                  isManualTare: aggregateIsManualTare,
                  grossImages: data.axles?.['axle-1']?.grossImages || [],
                  tareImages: data.axles?.['axle-1']?.tareImages || [],
                  axleWeights: newAxleWeights,
                },
              ])
            }
          }}
          disabled={disabled}
        />
      </Box>
    </>
  )
}

ActiveMaterialList.propTypes = {
  priceSheetsData: PropTypes.object,
  salesOrderData: PropTypes.object,
  onUpdateMaterial: PropTypes.func.isRequired,
  isUpdate: PropTypes.bool,
  materials: PropTypes.array,
  totalInformation: PropTypes.array,
  loading: PropTypes.bool,
  apiRef: PropTypes.object.isRequired,
  totalApiRef: PropTypes.object.isRequired,
  setFocus: PropTypes.func.isRequired,
  isTransfer: PropTypes.bool,
  disabled: PropTypes.bool,
}

ActiveMaterialList.defaultProps = {
  priceSheetsData: {},
  salesOrderData: null,
  isUpdate: false,
  materials: [],
  totalInformation: [],
  loading: false,
  isTransfer: false,
  disabled: false,
}

export default ActiveMaterialList
